/* src/components/Header.js */

import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  FaHome,
  FaInfoCircle,
  FaEnvelope,
  FaTools,
  FaChevronDown,
  FaCheckCircle,
  FaUser,
  FaUserCircle,
  FaUserCog,
  FaSignOutAlt,
} from "react-icons/fa";
import {
  MdComputer,
  MdSecurity,
} from "react-icons/md";
import { useAuth } from "../context/AuthContext";
import logo from "../assets/Images/favicon.png";
import "./Header.css";

function Header() {
  const location = useLocation();
  const { currentUser, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null); // Track which dropdown is open
  const navRef = useRef(null); // Keep navRef if needed for mobile menu logic, otherwise remove
  const servicesDropdownRef = useRef(null); // Ref for services dropdown content
  const userDropdownRef = useRef(null); // Ref for user dropdown content
  const servicesToggleRef = useRef(null); // Ref for services dropdown toggle button
  const headerRef = useRef(null); // Reference to the header element
  const dropdownCloseTimerRef = useRef(null);

  // Define all Services subpaths
  const servicesPaths = [
    "/services",
    "/it-health-check",
    "/it-consulting",
    "/computer-repair",
    "/managed-it-services",
    "/cloud-services",
    "/cybersecurity",
    "/data-backup-recovery",
    "/computer-repair-shreveport",
    "/managed-it-services-shreveport",
    "/it-support-accounting-firms",
    "/it-support-contractors",
    "/it-support-dental-offices",
    "/it-support-law-firms",
    "/it-support-medical-offices",
    "/it-support-restaurants",
    "/how-to-choose-it-support",
    "/managed-it-vs-break-fix",
    "/ransomware-protection",
    "/backup-readiness-review",
  ];

  // Determine if the current path is a Services subpath
  const isServicesPath = servicesPaths.includes(location.pathname);

  // Close menus on route change
  useEffect(() => {
    setMenuOpen(false);
    setActiveDropdown(null);
    // moveIndicatorToActive(); // Removed
  }, [location]); // Removed isServicesPath dependency as it's only used for indicator

  // Mobile menu owns scroll while open so the page behind it stays put.
  useEffect(() => {
    document.body.classList.toggle("mobile-nav-open", menuOpen);

    return () => {
      document.body.classList.remove("mobile-nav-open");
    };
  }, [menuOpen]);

  // Set header height CSS variable
  useLayoutEffect(() => {
    const calculateHeaderHeight = () => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight;
        document.documentElement.style.setProperty('--header-height', `${height}px`);
      }
    };

    // Initial calculation
    calculateHeaderHeight();
    
    // Recalculate on window resize
    window.addEventListener('resize', calculateHeaderHeight);
    
    // Set multiple timeouts to ensure header height is calculated correctly
    // after all content and styles are fully loaded
    const timeoutIds = [
      setTimeout(calculateHeaderHeight, 100),
      setTimeout(calculateHeaderHeight, 300),
      setTimeout(calculateHeaderHeight, 500),
      setTimeout(calculateHeaderHeight, 1000)
    ];
    
    return () => {
      window.removeEventListener('resize', calculateHeaderHeight);
      timeoutIds.forEach(id => clearTimeout(id));
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check for services dropdown
      if (
        activeDropdown === "services" &&
        servicesDropdownRef.current &&
        !servicesDropdownRef.current.contains(event.target) &&
        servicesToggleRef.current &&
        !servicesToggleRef.current.contains(event.target)
      ) {
        setActiveDropdown(null);
      }
      
      // Check for user dropdown
      if (
        activeDropdown === "user" &&
        userDropdownRef.current &&
        !userDropdownRef.current.contains(event.target) &&
        event.target.closest('.user-dropdown-toggle') === null
      ) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [activeDropdown]);

  // REMOVED moveIndicatorToActive function
  // REMOVED handleMouseEnter function (JS version)
  // REMOVED handleMouseLeave function (JS version)

  const handleDropdownMouseEnter = (dropdownName) => {
    if (dropdownCloseTimerRef.current) {
      clearTimeout(dropdownCloseTimerRef.current);
      dropdownCloseTimerRef.current = null;
    }
    setActiveDropdown(dropdownName);
  };

  const handleDropdownMouseLeave = () => {
    if (dropdownCloseTimerRef.current) {
      clearTimeout(dropdownCloseTimerRef.current);
    }
    dropdownCloseTimerRef.current = setTimeout(() => {
      setActiveDropdown(null);
      dropdownCloseTimerRef.current = null;
    }, 160);
  };

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
    setActiveDropdown(null); // Close any open dropdown when toggling main menu
  };

  const toggleDropdown = (dropdownName) => {
    // On mobile, we want to toggle the dropdown
    // On desktop with hover, we don't want to close it on click
    if (window.innerWidth <= 900) {
      setActiveDropdown(activeDropdown === dropdownName ? null : dropdownName);
    } else if (activeDropdown !== dropdownName) {
      // On desktop, only open if it's not already open
      setActiveDropdown(dropdownName);
    }
  };

  const handleLinkClick = () => {
    setMenuOpen(false);
    setActiveDropdown(null);
  };

  return (
    <div className="header-wrapper" ref={headerRef}>
      {/* Primary Navigation Bar */}
      <header className="primary-nav" role="banner">
        {/* Account tools stay available for signed-in users, but public visitors do not need an admin login in the marketing header. */}
        {currentUser && (
          <div className="sign-in-bar">
            <div 
              className={`user-dropdown ${activeDropdown === "user" ? "show" : ""}`}
              onMouseLeave={handleDropdownMouseLeave}
            >
              <button
                className="user-dropdown-toggle"
                onClick={() => toggleDropdown("user")}
                onMouseEnter={() => handleDropdownMouseEnter("user")}
                aria-haspopup="true"
                aria-expanded={activeDropdown === "user"}
              >
                <FaUserCircle className="icon-small" />
                <span className="user-greeting">Hello, {currentUser.name}</span>
                <FaChevronDown className={`arrow ${activeDropdown === "user" ? "rotate" : ""}`} />
              </button>
              <div className="user-dropdown-content" ref={userDropdownRef} aria-hidden={activeDropdown !== "user"}>
                {currentUser?.role === 'owner' && (
                  <div className="user-role-badge">Administrator</div>
                )}
                <Link to="/dashboard/home" className="dropdown-link" aria-label="Dashboard" tabIndex={activeDropdown === "user" ? 0 : -1}> 
                  <FaUser className="icon-small" /> Dashboard
                </Link>
                <Link to="/profile" className="dropdown-link" aria-label="Profile Settings" tabIndex={activeDropdown === "user" ? 0 : -1}>
                  <FaUserCog className="icon-small" /> Profile Settings
                </Link>
                <button onClick={logout} className="dropdown-link logout-button" aria-label="Logout" tabIndex={activeDropdown === "user" ? 0 : -1}>
                  <FaSignOutAlt className="icon-small" /> Logout
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="primary-nav-container">
          <Link to="/" className="logo-section" aria-label="Go to Home Page">
            {/* Logo Image */}
            <img src={logo} alt="Tharpe Industries Logo" className="App-logo" loading="lazy" />
            {/* Title and Other Header Content */}
            <div className="title-container">
              <div className="title-tharpe">THARPE</div>
              <div className="title-industries">INDUSTRIES</div>
            </div>
          </Link>

          {/* Hamburger icon for mobile */}
          <button
            className={`hamburger ${menuOpen ? 'active' : ''}`}
            onClick={toggleMenu}
            aria-label="Toggle navigation menu"
            aria-expanded={menuOpen}
          >
            <span className="bar"></span>
            <span className="bar"></span>
            <span className="bar"></span>
          </button>

            {/* Nav Menu */}
            <nav
              ref={navRef} // Keep navRef if needed for mobile menu logic
              className={`nav-menu ${menuOpen ? "show" : ""}`}
              // onMouseLeave={handleMouseLeave} // Removed
              aria-label="Primary Navigation"
            >
              {/* REMOVED sliding indicator div */}

            {/* Home Link */}
            <Link
              to="/"
              className={`nav-link ${location.pathname === "/" ? "active active-underline" : ""}`}
              // onMouseEnter={handleMouseEnter} // Removed
              onClick={handleLinkClick}
            >
              <FaHome className="icon" /> Home
            </Link>

            {/* Services with Dropdown */}
            <div 
              className={`dropdown ${activeDropdown === "services" ? "show" : ""}`} 
              onMouseEnter={() => handleDropdownMouseEnter("services")}
              onMouseLeave={handleDropdownMouseLeave} // Close when leaving the whole dropdown area
            >
              <button
                ref={servicesToggleRef} // Attach ref to toggle button
                className={`nav-link dropdown-toggle ${isServicesPath ? "active active-underline" : ""}`} // CSS handles underline via class
                onMouseEnter={() => handleDropdownMouseEnter("services")} // Open on entering the button
                aria-haspopup="true"
                aria-expanded={activeDropdown === "services"}
                onClick={() => toggleDropdown("services")}
                // onMouseEnter={handleMouseEnter} // Removed
              >
                <FaTools className="icon" /> Services <FaChevronDown className={`arrow ${activeDropdown === "services" ? "rotate" : ""}`} />
              </button>
              <div className="dropdown-content" ref={servicesDropdownRef} aria-hidden={activeDropdown !== "services"}>
                <Link
                  to="/services"
                  className={`dropdown-link ${location.pathname === "/services" ? "active" : ""}`}
                  onClick={handleLinkClick}
                  aria-label="All IT Services"
                  tabIndex={activeDropdown === "services" ? 0 : -1}
                >
                  <FaCheckCircle className="icon" /> All Services
                </Link>
                <div className="dropdown-divider" role="presentation" />

                <Link
                  to="/it-health-check"
                  className={`dropdown-link ${location.pathname === "/it-health-check" ? "active" : ""}`}
                  onClick={handleLinkClick}
                  aria-label="IT Health Check"
                  tabIndex={activeDropdown === "services" ? 0 : -1}
                >
                  <FaCheckCircle className="icon" /> IT Health Check
                </Link>
                <Link
                  to="/computer-repair"
                  className={`dropdown-link ${location.pathname === "/computer-repair" ? "active" : ""}`}
                  onClick={handleLinkClick}
                  aria-label="Computer Repair Services"
                  tabIndex={activeDropdown === "services" ? 0 : -1}
                >
                  <MdComputer className="icon" /> Computer Repair
                </Link>
                <Link
                  to="/managed-it-services"
                  className={`dropdown-link ${location.pathname === "/managed-it-services" ? "active" : ""}`}
                  onClick={handleLinkClick}
                  aria-label="Managed IT Services"
                  tabIndex={activeDropdown === "services" ? 0 : -1}
                >
                  <FaTools className="icon" /> Managed IT Services
                </Link>
                <Link
                  to="/cybersecurity"
                  className={`dropdown-link ${location.pathname === "/cybersecurity" ? "active" : ""}`}
                  onClick={handleLinkClick}
                  aria-label="Cybersecurity and Backup Services"
                  tabIndex={activeDropdown === "services" ? 0 : -1}
                >
                  <MdSecurity className="icon" /> Cybersecurity / Backup
                </Link>
              </div>
            </div>


            {/* About Link */}
            <Link
              to="/about"
              className={`nav-link ${location.pathname === "/about" ? "active active-underline" : ""}`} // Add active-underline for consistency
              // onMouseEnter={handleMouseEnter} // Removed
              onClick={handleLinkClick}
            >
              <FaInfoCircle className="icon" /> About
            </Link>

            {/* Contact Link */}
            <Link
              to="/contact-us"
              className={`nav-link nav-contact-cta ${location.pathname === "/contact-us" ? "active active-underline" : ""}`}
              // onMouseEnter={handleMouseEnter} // Removed
              onClick={handleLinkClick}
            >
              <FaEnvelope className="icon" /> Contact
            </Link>
          </nav>
        </div>
      </header>
    </div>
  );
}

export default Header;
